import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ChevronDown, Copy, Loader2, Plus } from 'lucide-react';

import { useAppDispatch, useAppSelector } from '../../hooks';
import { useStore } from '../../pages/workspace/hooks';
import {
  createEmptyVersion,
  deleteVersion,
  duplicateVersion,
  renameVersion,
} from '../../store/reducers/workspace.reducer';
import { green01 } from '../../theme';
import { JobStatus } from '../../types/project';
import { VersionHistoryList } from './sidebar/VersionHistoryList';

// Brand orange used by the in-app error/notification system (theme.ts
// `orangeError`, which is module-local — duplicated here as a one-line const
// so we stay consistent without forcing a theme export.
const WARN = '#FF8A00';

type LockState = 'draft' | 'computing' | 'baking' | 'completed' | 'failed';

/**
 * Reads the same fields VersionHistoryList uses for its per-row badge so the
 * pill and the dropdown stay in sync. Returns a single coarse state the pill
 * styles itself by.
 */
function deriveLockState(
  calc: JobStatus | undefined,
  autop: JobStatus | undefined,
  bake: JobStatus | undefined,
): LockState {
  if (calc === JobStatus.IN_PROGRESS || autop === JobStatus.IN_PROGRESS) return 'computing';
  if (bake === JobStatus.IN_PROGRESS) return 'baking';
  if (calc === JobStatus.COMPLETED && bake === JobStatus.COMPLETED) return 'completed';
  if (calc === JobStatus.FAILED || autop === JobStatus.FAILED || bake === JobStatus.FAILED) return 'failed';
  return 'draft';
}

interface PillVisuals {
  /** Segment background tint. */
  bg: string;
  /** Color for the version name (primary text). */
  textColor: string;
  /** Color for the sub-line status string. */
  statusColor: string;
  /** i18n key for the sub-line label. Translated at render time so the
   *  function itself stays pure and doesn't need to receive `t`. */
  statusKey: string;
  /** Optional small icon at the start of the status sub-line. */
  statusIcon?: React.ReactNode;
}

function visualsFor(state: LockState): PillVisuals {
  switch (state) {
    case 'completed':
      return {
        bg: 'rgba(179, 236, 73, 0.10)',
        textColor: green01,
        statusColor: green01,
        statusKey: 'workspace.versionStatus.completed',
      };
    case 'computing':
      return {
        bg: 'rgba(255, 255, 255, 0.04)',
        textColor: 'rgba(255, 255, 255, 0.95)',
        statusColor: 'rgba(255, 255, 255, 0.7)',
        statusKey: 'workspace.versionStatus.computing',
        statusIcon: <Loader2 size={10} className="animate-spin" />,
      };
    case 'baking':
      return {
        bg: 'rgba(255, 255, 255, 0.04)',
        textColor: 'rgba(255, 255, 255, 0.95)',
        statusColor: 'rgba(255, 255, 255, 0.7)',
        statusKey: 'workspace.versionStatus.baking',
        statusIcon: <Loader2 size={10} className="animate-spin" />,
      };
    case 'failed':
      return {
        bg: 'rgba(255, 138, 0, 0.10)',
        textColor: WARN,
        statusColor: WARN,
        statusKey: 'workspace.versionStatus.failed',
      };
    default:
      return {
        bg: 'transparent',
        textColor: 'rgba(255, 255, 255, 0.95)',
        statusColor: 'rgba(255, 255, 255, 0.55)',
        statusKey: 'workspace.versionStatus.draft',
      };
  }
}

/**
 * Navbar cluster: status pill on the left (current version + lock tag), and
 * a tall split-button block on the right with the two top-level mutations
 * (new blank version on top, duplicate the current one on bottom). The pill
 * itself opens the version dropdown via its inline chevron; everything else
 * about the pill is read-only status.
 */
export function WorkspaceVersionBar() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { project, activeVersion, setShowAutoPositionSidebar } = useStore();
  const isCreatingVersion = useAppSelector((state) => state.workspace.isCreatingVersion);

  const [showDropdown, setShowDropdown] = useState(false);
  // Inline rename of the active version straight from the navbar pill —
  // mirrors how the project name in the navbar handles double-click.
  const [isInlineEditing, setIsInlineEditing] = useState(false);
  const [inlineEditName, setInlineEditName] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showDropdown) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showDropdown]);

  const handleVersionChange = useCallback(
    (versionId: number) => {
      if (!project) return;
      navigate(`/workspace?projectId=${project.id}&versionId=${versionId}`);
      setShowDropdown(false);
    },
    [project, navigate],
  );

  const handleNewBlank = useCallback(() => {
    if (!project || !activeVersion || isCreatingVersion) return;
    dispatch(createEmptyVersion({ projectId: project.id, sourceVersionId: activeVersion.id }));
    setShowDropdown(false);
    setShowAutoPositionSidebar(false);
  }, [project, activeVersion, dispatch, setShowAutoPositionSidebar, isCreatingVersion]);

  const handleDuplicateCurrent = useCallback(() => {
    if (!project || !activeVersion || isCreatingVersion) return;
    dispatch(duplicateVersion({ projectId: project.id, sourceVersionId: activeVersion.id }));
    setShowDropdown(false);
    setShowAutoPositionSidebar(false);
  }, [project, activeVersion, dispatch, setShowAutoPositionSidebar, isCreatingVersion]);

  const handleDelete = useCallback(
    (versionId: number) => {
      if (!project) return;
      dispatch(deleteVersion({ projectId: project.id, versionId }));
      setShowDropdown(false);
    },
    [project, dispatch],
  );

  const handleRename = useCallback(
    (versionId: number, newName: string) => {
      if (!project || !newName.trim()) return;
      dispatch(renameVersion({ projectId: project.id, versionId, newName: newName.trim() }));
    },
    [project, dispatch],
  );

  const startInlineEdit = () => {
    if (!activeVersion) return;
    setInlineEditName(activeVersion.name);
    setIsInlineEditing(true);
  };

  const commitInlineEdit = () => {
    if (activeVersion && inlineEditName.trim() && inlineEditName.trim() !== activeVersion.name) {
      handleRename(activeVersion.id, inlineEditName.trim());
    }
    setIsInlineEditing(false);
    setInlineEditName('');
  };

  const cancelInlineEdit = () => {
    setIsInlineEditing(false);
    setInlineEditName('');
  };

  if (!project || !activeVersion) return null;

  const state = deriveLockState(
    activeVersion.calculationJob?.jobStatus,
    activeVersion.autopositioningJob?.jobStatus,
    activeVersion.renderBakingJob?.jobStatus,
  );
  const v = visualsFor(state);

  // Surface how many versions exist + which one is active so the user knows
  // the chevron actually opens something. Without this counter people miss
  // that other versions exist at all and never click the dropdown.
  const totalVersions = project.versions?.length ?? 0;
  // Counter only shows when there's actually more than one version — its
  // job is to telegraph "you can switch". A "{n} versions" label reads
  // more naturally than the previous "i/N" fraction.
  const versionCounter = totalVersions > 1 ? t('workspace.versionCount', { count: totalVersions }) : null;

  return (
    <div ref={containerRef} className="relative flex items-stretch h-full">
      {/* ── Version status pill (two-line: name on top, status sub-line).
            Fixed width so the action buttons to the right stay anchored
            regardless of how long the version name is — names just truncate
            inside the reserved space. ──────────────────────────────────── */}
      <div
        className="flex items-center gap-2 px-3"
        style={{
          width: 270,
          background: v.bg,
          borderLeft: '1px solid rgba(255, 255, 255, 0.08)',
        }}
      >
        <div className="flex flex-col justify-center gap-0.5 min-w-0 flex-1">
          {isInlineEditing ? (
            <input
              type="text"
              value={inlineEditName}
              onChange={(e) => setInlineEditName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitInlineEdit();
                else if (e.key === 'Escape') cancelInlineEdit();
              }}
              onBlur={commitInlineEdit}
              autoFocus
              className="text-[15px] font-medium leading-none bg-black border border-lime-500 rounded px-1.5 py-0.5 focus:outline-none w-full"
              style={{ color: v.textColor }}
            />
          ) : (
            <span
              className="text-[15px] font-medium leading-none truncate cursor-text"
              style={{ color: v.textColor }}
              title={activeVersion.name}
              onDoubleClick={startInlineEdit}
            >
              {activeVersion.name}
            </span>
          )}
          <span
            className="inline-flex items-center gap-1 text-[11px] font-medium leading-none"
            style={{ color: v.statusColor }}
          >
            {v.statusIcon}
            {t(v.statusKey)}
          </span>
        </div>

        <button
          onClick={() => setShowDropdown((s) => !s)}
          title={versionCounter ? `${t('workspace.switchVersion')} (${totalVersions})` : t('workspace.switchVersion')}
          className="ml-1 inline-flex items-center gap-1 px-1.5 py-1 rounded hover:bg-white/10 transition-colors"
          aria-label={t('workspace.switchVersion')}
          aria-expanded={showDropdown}
        >
          {versionCounter && (
            <span
              className="text-[10px] font-semibold tabular-nums leading-none"
              style={{ color: v.textColor, opacity: 0.7 }}
            >
              {versionCounter}
            </span>
          )}
          <ChevronDown
            size={14}
            className={`transition-transform ${showDropdown ? 'rotate-180' : ''}`}
            style={{ color: v.textColor, opacity: 0.7 }}
          />
        </button>
      </div>

      {/* ── Split action block (top: new, bottom: duplicate) ────────────── */}
      <div
        className="flex flex-col"
        style={{
          borderLeft: '1px solid rgba(255, 255, 255, 0.08)',
          borderRight: '1px solid rgba(255, 255, 255, 0.08)',
        }}
      >
        <button
          onClick={handleNewBlank}
          disabled={isCreatingVersion}
          className="flex-1 flex items-center gap-1.5 px-3 text-[12px] font-medium text-white/85 hover:text-white transition-colors disabled:cursor-not-allowed disabled:text-white/50"
          style={{ background: 'rgb(69 69 69)' }}
          title={t('workspace.versionDropdown.createNewVersion')}
        >
          {isCreatingVersion ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          <span>{t('workspace.versionDropdown.createNewVersion')}</span>
        </button>
        <div style={{ height: 1, background: 'rgba(255, 255, 255, 0.08)' }} />
        <button
          onClick={handleDuplicateCurrent}
          disabled={isCreatingVersion}
          className="flex-1 flex items-center gap-1.5 px-3 text-[12px] font-medium text-white/85 hover:text-white transition-colors disabled:cursor-not-allowed disabled:text-white/50"
          style={{ background: 'rgb(69 69 69)' }}
          title={t('workspace.versionDropdown.duplicateToEdit')}
        >
          {isCreatingVersion ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} />}
          <span>{t('workspace.versionDropdown.duplicateToEdit')}</span>
        </button>
      </div>

      {/* ── Dropdown panel: pure switch / rename / delete. Anchored to the
            pill's chevron, opens below the navbar. ─────────────────────── */}
      {showDropdown && project.versions && (
        <div
          className="absolute top-full right-0 mt-1 z-50 min-w-[440px]"
          // Hard cap at half the viewport width so the dropdown can't run
          // off-screen on narrow displays. `min(760px, 50vw)` keeps the
          // earlier 760px target for wide monitors and the 50vw rule kicks
          // in below ~1520px viewports.
          style={{ maxWidth: 'min(760px, 50vw)' }}
        >
          <VersionHistoryList
            versions={project.versions}
            activeVersionId={project.activeVersionId || 0}
            onVersionChange={handleVersionChange}
            onDeleteVersion={handleDelete}
            onRenameVersion={handleRename}
            onCloseAutoPosition={() => setShowAutoPositionSidebar(false)}
          />
        </div>
      )}
    </div>
  );
}
